import { api, query } from '@organization/api'
import { mainAxios } from '@organization/api/axios/mainAxios'
import { OrganizationRole } from '@organization/api/requests'
import { APPLICATIONS_URL, LOGIN_URL } from '@organization/app-constants'
import { UserSessionContext } from '@shared/context'
import {
  clearLocalStorage,
  getLocalStorageTokens,
  getTokenFromUrl,
  setAppToken,
  setLocalStorageToken
} from '@shared/utils/getToken'

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { App } from 'antd'
import { PropsWithChildren, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

const { access, refresh } = getLocalStorageTokens()

const getPrivatePath = (path: string) => {
  return [LOGIN_URL].includes(path) ? APPLICATIONS_URL : path
}

export const SessionContextProvider = ({ children }: PropsWithChildren) => {
  const navigate = useNavigate()
  const { pathname, search } = useLocation()
  const queryClient = useQueryClient()
  const { message } = App.useApp()
  const { t } = useTranslation()

  const tokenFromUrl = getTokenFromUrl()
  const storedToken = tokenFromUrl || access || refresh

  const {
    data: userBroker,
    isLoading,
    refetch
  } = useQuery({
    queryKey: [query.users.get().queryKey],
    queryFn: async () => {
      const user = await api.users.get()

      const insuranceInstance = user?.public_applications?.find(
        ({ instance_type, token }) => {
          setAppToken(instance_type.type, token)
          return instance_type.type === 'insurance'
        }
      )

      if (insuranceInstance) {
        const { data } = await mainAxios.get('/users/users/profile/', {
          baseURL: insuranceInstance.domain,
          headers: { 'Saas-App-Token': insuranceInstance.token }
        })

        return {
          user,
          profile: data,
          ...(data?.broker && {
            broker: { user: data, token: insuranceInstance.token }
          })
        }
      }

      return { user }
    },
    enabled: !!storedToken,
    retry: false
  })

  //todo: implement cookie and token refresh

  const { mutate: _mutate } = useMutation({
    mutationFn: api.auth.checkCookie,

    onSuccess: async (data) => {
      setLocalStorageToken(data)
      await refetch()
      navigate(`${getPrivatePath(pathname)}${search}`)
    }
  })

  const removeUserData = () => {
    clearLocalStorage()
    queryClient.clear()
    navigate(LOGIN_URL)
  }

  const contextValue = {
    profile: userBroker?.profile,
    broker: userBroker?.broker,
    user: userBroker?.user,
    isAdmin:
      userBroker?.user?.role === OrganizationRole.ADMIN ||
      userBroker?.user?.role === OrganizationRole.OWNER,
    isLoading: isLoading,
    updateUserData: async () => {
      const { data } = await refetch()

      return data
    },
    removeUserData
  }

  useEffect(() => {
    if (storedToken) {
      refetch()
    }
  }, [storedToken])

  useEffect(() => {
    if (!userBroker) return
    if (contextValue.isAdmin) return
    if (contextValue.broker) return
    if (userBroker?.user?.pinned_applications?.length) return

    message.error(t('you_have_no_active_applications'))

    removeUserData()
  }, [userBroker])

  return (
    <UserSessionContext.Provider value={contextValue}>
      {children}
    </UserSessionContext.Provider>
  )
}
